import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatTimeAgo } from '../lib/utils';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Bot, User, Send, Loader2, Plus, Headphones, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('ai');
  const [humanTickets, setHumanTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [humanInput, setHumanInput] = useState('');

  const fetchHumanTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support/human/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHumanTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets');
    }
  }, [token]);

  useEffect(() => {
    fetchHumanTickets();
  }, [fetchHumanTickets]);

  const sendAiMessage = async (e) => {
    e.preventDefault();
    if (!aiInput.trim() || aiLoading) return;

    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: aiInput,
      created_at: new Date().toISOString()
    };

    setAiMessages(prev => [...prev, userMessage]);
    setAiInput('');
    setAiLoading(true);

    try {
      const response = await axios.post(`${API}/support/ai/chat`, {
        message: aiInput,
        session_id: aiSessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAiSessionId(response.data.session_id);
      
      const aiMessage = {
        id: response.data.message_id,
        role: 'assistant',
        content: response.data.response,
        created_at: new Date().toISOString()
      };

      setAiMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('AI is not available right now');
      setAiMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setAiLoading(false);
    }
  };

  const createHumanTicket = async () => {
    try {
      const response = await axios.post(`${API}/support/human/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveTicket(response.data);
      setHumanTickets(prev => [response.data, ...prev]);
      toast.success('Ticket created!');
    } catch (error) {
      toast.error('Error');
    }
  };

  const sendHumanMessage = async (e) => {
    e.preventDefault();
    if (!humanInput.trim() || !activeTicket) return;

    try {
      await axios.post(`${API}/support/human/message`, {
        message: humanInput,
        support_ticket_id: activeTicket.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setActiveTicket(prev => ({
        ...prev,
        messages: [...prev.messages, {
          id: Date.now().toString(),
          role: 'user',
          content: humanInput,
          created_at: new Date().toISOString()
        }]
      }));
      setHumanInput('');
    } catch (error) {
      toast.error('Error');
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages, activeTicket?.messages]);

  return (
    <div className="max-w-4xl mx-auto h-screen flex flex-col" data-testid="support-page">
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Headphones className="w-6 h-6 text-primary" />
            <h1 className="font-bold text-lg">Support</h1>
          </div>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start border-b border-border/50 rounded-none bg-transparent h-12 p-0 px-4">
          <TabsTrigger value="ai" className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent gap-2">
            <Sparkles className="w-4 h-4" />
            AI Support
          </TabsTrigger>
          <TabsTrigger value="human" className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent gap-2">
            <User className="w-4 h-4" />
            Human Support
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="flex-1 flex flex-col m-0">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {aiMessages.length === 0 && (
              <div className="text-center py-12">
                <Bot className="w-16 h-16 mx-auto text-primary mb-4" />
                <h2 className="text-xl font-bold">Welcome to AI Support!</h2>
                <p className="text-muted-foreground mt-2">I'm here to help you with any questions about FityH.</p>
              </div>
            )}

            {aiMessages.map(msg => (
              <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
                  {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`max-w-[75%] p-4 rounded-2xl ${msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-secondary rounded-bl-md'}`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {aiLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-secondary p-4 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendAiMessage} className="p-4 border-t border-border/50">
            <div className="flex gap-2">
              <Input value={aiInput} onChange={(e) => setAiInput(e.target.value)} placeholder="Ekri mesaj ou..." className="flex-1 rounded-full" data-testid="ai-chat-input" />
              <Button type="submit" size="icon" className="rounded-full" disabled={aiLoading || !aiInput.trim()} data-testid="ai-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="human" className="flex-1 flex flex-col m-0">
          {!activeTicket ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Headphones className="w-16 h-16 mx-auto text-primary mb-4" />
                <h2 className="text-xl font-bold">Human Support</h2>
                <p className="text-muted-foreground mt-2">Create a ticket to speak with a supervisor.</p>
                <p className="text-sm text-muted-foreground mt-1">Or send an email: <a href="mailto:fityhapp@gmail.com" className="text-primary font-medium">fityhapp@gmail.com</a></p>
                <Button onClick={createHumanTicket} className="mt-6 rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Ticket
                </Button>
                {humanTickets.length > 0 && (
                  <div className="mt-6 space-y-2">
                    {humanTickets.map(ticket => (
                      <button key={ticket.id} onClick={() => setActiveTicket(ticket)} className="w-full text-left p-3 rounded-xl hover:bg-secondary">
                        <span className="text-sm">#{ticket.id.slice(0, 8)}</span>
                        <p className="text-xs text-muted-foreground">{formatTimeAgo(ticket.updated_at, 'ht')}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-border/50">
                <p className="text-sm text-muted-foreground">Ticket #{activeTicket.id.slice(0, 8)}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {activeTicket.messages?.map(msg => (
                  <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-green-500 text-white'}`}>
                      {msg.role === 'user' ? <User className="w-4 h-4" /> : <Headphones className="w-4 h-4" />}
                    </div>
                    <div className={`max-w-[75%] p-4 rounded-2xl ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendHumanMessage} className="p-4 border-t border-border/50">
                <div className="flex gap-2">
                  <Input value={humanInput} onChange={(e) => setHumanInput(e.target.value)} placeholder="Ekri mesaj ou..." className="flex-1 rounded-full" data-testid="human-chat-input" />
                  <Button type="submit" size="icon" className="rounded-full" disabled={!humanInput.trim()} data-testid="human-send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
